import { SectionHeader } from '@/components/AssetsPage/SectionHeader';
import { BodyText } from '@/components/BodyText';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useGetPartFinderReport } from '@/hooks/api/part-finder-reports/useGetPartFinderReport';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { ChevronRight, HelpOutline } from '@mui/icons-material';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';
import { ManufacturersTable } from './components/ManufacturersTable';
import { NsnSummaryTable } from './components/NsnSummaryTable';
import { ReportParametersTable } from './components/ReportParametersTable';
import { TechnicalCharacteristicsTable } from './components/TechnicalCharacteristicsTable';
import { SourcingOpportunitiesTable } from './components/SourcingOpportunitiesTable';
import { ProcurementOptionsTable } from './components/ProcurementOptionsTable';
import { ReportImages } from './components/ReportImages';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
});

const ContentWrapper = styled(Box)({
  paddingRight: '6px',
  overflowY: 'auto',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const ReportSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: '12px',
  marginBottom: '16px',
  width: '100%',
  maxWidth: '950px',
}));

const SubsectionHeader = styled(BodyText)(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.secondary,
  marginBottom: '8px',
}));

const SubsectionHeaderWithIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '8px',
}));

const HelpIcon = styled(HelpOutline)(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.text.secondary,
  cursor: 'help',
}));

const formatReportDate = (date: string | Date): string => format(new Date(date), 'MMM d, yyyy');

const hasItems = <T,>(array: T[] | undefined): array is T[] => array !== undefined && array.length > 0;

export const PartFinderReportPage = () => {
  const showMockSections = false;
  const { id } = useParams<{ id: string }>();
  const { data: report, isLoading, error } = useGetPartFinderReport(id || '');
  const { getPath } = useTenants();
  usePageTitle(report?.partNumber ? `${report?.partNumber} - Part Finder Report` : 'Part Finder Report');
  const header = (
    <SectionHeader>
      <Link to={getPath('part-finder')}>
        <BodyText>Part Finder</BodyText>
      </Link>
      {report && (
        <>
          <ChevronRight sx={{ fontSize: '16px' }} />
          <BodyText>
            {report.partNumber} {formatReportDate(report.createdAt)}
          </BodyText>
        </>
      )}
    </SectionHeader>
  );

  if (isLoading) {
    return (
      <PageContainer>
        {header}
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !report) {
    return (
      <PageContainer>
        {header}
        <ContentWrapper>
          <ApiErrorAlert error={getApiError(error, 'Failed to load report')} />
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {header}
      <ContentWrapper>
        <ReportSection>
          <ReportParametersTable
            partNumber={report.partNumber}
            description={report.description}
            status={report.status}
            creatorEmail={report.creator?.email}
          />
        </ReportSection>

        {report.sourcingOpportunities && (
          <ReportSection>
            <SubsectionHeaderWithIcon>
              <SubsectionHeader sx={{ marginBottom: 0 }}>Sourcing Opportunities</SubsectionHeader>
              <Tooltip title='These sourcing opportunities are AI-generated suggestions. Please conduct your own due diligence to verify supplier details, pricing, and availability before making procurement decisions.'>
                <HelpIcon />
              </Tooltip>
            </SubsectionHeaderWithIcon>
            <SourcingOpportunitiesTable opportunities={report.sourcingOpportunities} />
          </ReportSection>
        )}

        {showMockSections && (
          <ReportSection>
            <SubsectionHeader>Procurement Options</SubsectionHeader>
            <ProcurementOptionsTable />
          </ReportSection>
        )}

        {showMockSections && (
          <ReportSection>
            <SubsectionHeader>Images</SubsectionHeader>
            <ReportImages />
          </ReportSection>
        )}

        {report.nsnReport && (
          <ReportSection>
            <SubsectionHeader>Summary</SubsectionHeader>
            <NsnSummaryTable nsnReport={report.nsnReport} />
          </ReportSection>
        )}

        {hasItems(report.nsnReport?.manufacturers) && (
          <ReportSection>
            <SubsectionHeader>Manufacturers</SubsectionHeader>
            <ManufacturersTable manufacturers={report.nsnReport.manufacturers} />
          </ReportSection>
        )}

        {hasItems(report.nsnReport?.technicalCharacteristics) && (
          <ReportSection>
            <SubsectionHeader>Technical Characteristics</SubsectionHeader>
            <TechnicalCharacteristicsTable technicalCharacteristics={report.nsnReport.technicalCharacteristics} />
          </ReportSection>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};
